import { useQueryPagination } from "@/services/adapterPaginations";
import { useQueryListToSelect } from "@/services/adapters/queryListToSelect";
import { api } from "@/services/api";
import { useToast } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const PERMISSAO_LIST_QUERY_PREFIX = "query-list-dependencepermissao-usuario";

export const useServicePermission = () => {
  const toast = useToast();
  const nav = useNavigate();
  const queryClient = useQueryClient();

  const invalidatePermissaoList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [PERMISSAO_LIST_QUERY_PREFIX] });
  }, [queryClient]);

  const { id } = useParams();

  const [userSelected, setUserSelected] = useState<number>(0);
  const [userToReceived, setUserToReceived] = useState<number>(0);
  const [clonerSelected, setClonerSelected] = useState<number>(0);

  // Estados para controle de paginação
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [extraParams, setExtraParams] = useState<Record<string, any>>({});

  const [isCloning, setIsCloning] = useState<boolean>(false);
  const [menuSelect, setMenuSelect] = useState<any>();
  const [rotinaSelect, setRotinaSelect] = useState<any>();
  const [funcaoSelect, setFuncaoSelect] = useState<any>();

  const onOpenAdd = () => {
    nav(userSelected + "/new");
  };
  const onOpenClone = () => {
    nav(userSelected + "/clone");
  };

  const submitPermissao = async () => {
    try {
      await api.post("permissao", {
        ativo: true,
        funcionalidade: funcaoSelect?.value,
        rotina: rotinaSelect?.value,
        usuario: userSelected || id,
      });

      toast({
        title: "Sucesso",
        status: "success",
      });
    } catch (error: any) {
      toast({
        description: JSON.stringify(
          error?.response?.data?.mensagem || "Erro ao realizar operação"
        ),
        title: "Erro!",
        status: "error",
      });
    } finally {
      refetch();
      invalidatePermissaoList();
    }
  };

  const { list: listMenu } = useQueryListToSelect("menu");
  const { list: listRotina } = useQueryListToSelect("rotina");
  const { list: listFuncao } = useQueryListToSelect("funcionalidade");
  const { list: listUsuario } = useQueryListToSelect("usuario/geral");

  // Opção 1: Passando page e size como parâmetros adicionais
  const {
    data: listRotinas = [],
    refetch,
    allPage,
    page,
    setPage,
  } = useQueryPagination(
    "permissao/filtro",
    {
      usuario: userSelected,
      page: currentPage,
      size: pageSize,
      ...extraParams, // Permite parâmetros adicionais personalizados
    },
    "permissao-usuario"
  );

  // Função para alterar página
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Função para alterar tamanho da página
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0); // Reset para primeira página quando mudar o tamanho
  };

  // Função para adicionar parâmetros extras na consulta
  const updateQueryParams = (params: Record<string, any>) => {
    setExtraParams(params);
    setCurrentPage(0); // Reset para primeira página
  };

  const updateActive = useCallback(
    async (perm: any) => {
      try {
        await api.put("permissao/" + perm?.id, { ativo: !perm?.ativo });
        toast({
          title: "Sucesso",
          status: "success",
        });
      } catch (error: any) {
        toast({
          description: JSON.stringify(error?.response?.data?.mensagem),
          title: "Erro!",
          status: "error",
        });
      } finally {
        refetch();
        invalidatePermissaoList();
      }
    },
    [refetch, toast, invalidatePermissaoList]
  );

  const submitClonagem = async () => {
    try {
      setIsCloning(true);
      if (!userToReceived || !clonerSelected) {
        throw new Error(
          "Por favor, selecione tanto o usuário alvo quanto o usuário a clonar."
        );
      }

      if (userToReceived === clonerSelected) {
        throw new Error(
          "O usuário alvo e o usuário a clonar devem ser diferentes."
        );
      }

      const usuariosValidos = new Set(listUsuario?.map((u: any) => u.value));
      if (!usuariosValidos.has(clonerSelected) || !usuariosValidos.has(userToReceived)) {
        throw new Error(
          "Um dos usuários selecionados não foi encontrado (pode ter sido excluído). Selecione novamente."
        );
      }

      const response = await api.get("permissao/filtro", {
        params: { usuario: clonerSelected, page: 0, size: 1000 },
      });
      const clonerPermissions: any[] = response.data.paginacao;

      const clonerPermissionsAtivas = (clonerPermissions || []).filter(
        (perm: any) => perm.ativo
      );

      if (clonerPermissionsAtivas.length === 0) {
        throw new Error(
          "O usuário selecionado para clonagem não possui permissões ativas."
        );
      }

      const responseReceived = await api.get("permissao/filtro", {
        params: { usuario: userToReceived, page: 0, size: 1000 },
      });
      const receivedPermissions: any[] = responseReceived.data.paginacao;

      const permissoesExistentesDestino: Record<number, { idPermissao: number; ativo: boolean }> = {};
      receivedPermissions.forEach((perm: any) => {
        permissoesExistentesDestino[perm.rotina.id] = {
          idPermissao: perm.id,
          ativo: perm.ativo,
        };
      });

      const permissionsToCreate = clonerPermissionsAtivas.filter(
        (perm: any) => !permissoesExistentesDestino[perm.rotina.id]
      );
      const permissionsToReactivate = clonerPermissionsAtivas.filter(
        (perm: any) =>
          permissoesExistentesDestino[perm.rotina.id] &&
          !permissoesExistentesDestino[perm.rotina.id].ativo
      );

      if (permissionsToCreate.length === 0 && permissionsToReactivate.length === 0) {
        toast({
          title: "Nenhuma nova permissão para clonar.",
          description:
            "O usuário alvo já possui ativas todas as permissões do usuário clonador.",
          status: "info",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const clonePromises = [
        ...permissionsToCreate.map((perm: any) =>
          api.post("permissao", {
            ativo: true,
            rotina: perm.rotina.id,
            usuario: userToReceived,
          })
        ),
        ...permissionsToReactivate.map((perm: any) =>
          api.put(`permissao/${permissoesExistentesDestino[perm.rotina.id].idPermissao}`, {
            ativo: true,
            rotina: perm.rotina.id,
            usuario: userToReceived,
          })
        ),
      ];

      const results = await Promise.allSettled(clonePromises);

      const fulfilled = results.filter(
        (result) => result.status === "fulfilled"
      ).length;
      const rejected = results.filter(
        (result) => result.status === "rejected"
      ).length;

      if (fulfilled > 0) {
        toast({
          title: "Clonagem realizada com sucesso.",
          description: `${fulfilled} permissões clonadas.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      if (rejected > 0) {
        toast({
          title: "Clonagem parcialmente concluída.",
          description: `${rejected} permissões não foram clonadas.`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      toast({
        title: "Erro durante a clonagem.",
        description: error.message || "Ocorreu um erro inesperado.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCloning(false);
      refetch();
      invalidatePermissaoList();
    }
  };

  return {
    submitClonagem,
    listMenu,
    listRotina,
    listFuncao,
    updateActive,
    menuSelect,
    isCloning,
    listRotinas,
    listUsuario,
    submitPermissao,
    userSelected,
    setUserSelected,
    userToReceived,
    setUserToReceived,
    clonerSelected,
    setClonerSelected,
    setMenuSelect,
    setRotinaSelect,
    setFuncaoSelect,
    onOpenAdd,
    onOpenClone,
    rotinaSelect,
    funcaoSelect,
    // Novos retornos para controle de paginação
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    setCurrentPage,
    setPageSize,
    updateQueryParams,
    extraParams,
    allPage,
    page,
    setPage,
  };
};
